/**
 @Description: Axios统一配置
 @Document: http://www.axios-js.com/zh-cn/docs/ Axios中文文档
 */
import Axios from "axios"
import { Loading, Message, Notification } from "element-ui"
import Cookies from "js-cookie"
import NProgress from "nprogress"
import { PROXY_PREFIX } from "../../../../global-constant"
import { EnumApiCode } from "../../../../globalEnum"
import { handleErrorHttpCode } from "./cacth-http-error"
import "nprogress/nprogress.css"
// 加载进度条配置
NProgress.configure(
  { showSpinner: false },
  {
    ease: "ease",
    speed: 600
  }
)

class HttpService {
  // 自定义的配置项 用于子项目实例化传递的各种自定义配置
  constructor(customConfig) {
    // 用于限制错误弹窗的频率 优化用户体验
    this.messageDelayFlag = false
    // 配置默认值
    this.customConfig = {
      timeout: 30000,
      succeedCodes: [EnumApiCode.Success], // 设置默认的请求成功code数组
      baseURL: PROXY_PREFIX,
      tokenKey: "TokenNew", // 设置默认的身份验证tokenKey
      languageKey: "language", // 设置默认的身份验证tokenKey
      errMsgDelay: 3000, // 错误弹窗的频率(默认接口错误3秒内只弹出一次)
      ...customConfig // 如果子项目实例化有自定义设置, 则覆盖上面的默认值
    }
    this.instance = Axios.create({
      baseURL: this.customConfig.baseURL,
      headers: { "Content-Type": "application/json;charset=utf-8" },
      timeout: this.customConfig.timeout
    })
    this.interceptors()
  }

  /**
   * 在恢复错误弹窗提示
   * @param timeout
   */
  resetMessageDelayFlag(timeout = this.customConfig.errMsgDelay) {
    setTimeout(() => {
      this.messageDelayFlag = false
    }, timeout)
  }

  interceptors() {
    NProgress.start()
    this.instance.interceptors.request.use(
      (config) => {
        const { tokenKey, languageKey } = this.customConfig
        // axios默认的config没有source类型 使用断言类型添加
        const rewriteConfig = config
        rewriteConfig.source = Axios.CancelToken.source()
        rewriteConfig.cancelToken = rewriteConfig?.source.token
        // 让每个请求携带自定义token身份认证 可通过实例化选项配置tokenKey
        if (localStorage.getItem(tokenKey) && !rewriteConfig.headers.Authorization) {
          rewriteConfig.headers.Authorization = `Bearer ${localStorage.getItem(tokenKey)}`
        }
        // 配置国际化 通过Cookies实时获取
        rewriteConfig.headers.lang = localStorage.getItem(languageKey) || "zh-CN"
        // 检查是否重复请求
        // checkRequest(rewriteConfig)
        return config
      },
      (error) => Promise.reject(error)
    )
    this.instance.interceptors.response.use(
      (res) => {
        NProgress.done()
        // 删除已成功的请求key
        // 如果响应结果为200 直接返回结果
        if (res.status === 200) {
          return Promise.resolve(res)
        }
      },
      (err) => {
        if('ECONNABORTED' == err.code) {
          Message({
            message: `请求超时`,
            type: 'error',
            duration: 5000
          })
        }
        // 对http状态码错误进行重复拦截 防止多个请求连续弹窗提示错误和执行回调函数
        if (!this.messageDelayFlag) {
          // 非200情况下 捕获http状态码错误信息 抛出异常提示
          this.messageDelayFlag = true
          handleErrorHttpCode(err.response.status)
          this.resetMessageDelayFlag()
        }
        // 接口错误时关闭element库的loading 防止全局loading效果不关闭
        const loadingInstance = Loading.service({ fullscreen: true })
        loadingInstance.close()
        return Promise.reject(err)
      }
    )
  }

  /**
   * 处理错误 弹窗提示接口返回的msg 请求方法的实现中使用isAutoCatchStatus参数决定自动弹出错误还是在接口响应时由调用者手动处理
   */
  static errCatch(res, reject) {
    Notification({
      message: res.message ?? "未捕获错误信息，请向管理人员反馈"
    })
    HttpService.messageDelayFlag = false
    reject(res)
  }

  /**
   * 对接口返回的response进行处理, 判断是否成功, 是否需要手动处理错误
   * @param processParams
   */
  responseProcess(processParams) {
    const { method, url, isAutoCatchStatus, data, params, headers } = processParams
    return new Promise((resolve, reject) => {
      this.instance({
        url,
        method,
        data,
        params,
        headers
      })
        .then((res) => {
          const { data: resData } = res
          const { tokenExpirationCb } = this.customConfig
          // 当isAutoCatch为true时 自动弹出错误提示
          if (resData.code === 10004 && tokenExpirationCb) {
            // tokenExpirationCb?.()
            // reject(res)
            if (window.location.href.indexOf("/home") == -1) {
              tokenExpirationCb?.()
            }
            reject(res)
          }
          if (isAutoCatchStatus) {
            const isSuccess = this.customConfig.succeedCodes.includes(resData.code)
            if (isSuccess) {
              // 请求code为成功 返回resData
              resolve(resData)
            } else {
              // 对接口错误进行重复拦截 防止多个请求连续弹窗提示错误和执行回调函数
              if (!this.messageDelayFlag) {
                this.messageDelayFlag = true
                Message.error(resData.msg)
                this.resetMessageDelayFlag()
              }
              return reject(resData.data)
            }
          } else {
            // isAutoCatch配置为false 则直接返回resData 由调用者手动处理错误
            resolve(resData)
          }
        })
        .catch((res) => {
          if (!this.messageDelayFlag) {
            this.messageDelayFlag = true
            HttpService.errCatch(res, reject)
          }
        })
    })
  }

  /**
   * GET 请求
   * @param url
   * @param params
   * @param methodsConfig
   */
  get(url, params = {}, methodsConfig = {}) {
    const { headers, isAutoCatchStatus = true } = methodsConfig
    return this.responseProcess({
      method: "GET",
      url,
      params,
      headers,
      isAutoCatchStatus
    })
  }

  /**
   * POST请求
   * @param url
   * @param data
   * @param methodsConfig
   */
  post(url, data = {}, methodsConfig = {}) {
    const { headers, isAutoCatchStatus = true } = methodsConfig
    return this.responseProcess({
      method: "POST",
      url,
      data,
      headers,
      isAutoCatchStatus
    })
  }

  /**
   * POST请求params传参
   * @param url
   * @param data
   * @param methodsConfig
   */
  postParams(url, data = {}, methodsConfig = {}) {
    const { headers, isAutoCatchStatus = true } = methodsConfig
    return this.responseProcess({
      method: "POST",
      url,
      params: data,
      headers,
      isAutoCatchStatus
    })
  }

  /**
   * PUT请求
   * @param url
   * @param data
   * @param methodsConfig
   */
  put(url, data = {}, methodsConfig = {}) {
    const { headers, isAutoCatchStatus = true } = methodsConfig
    return this.responseProcess({
      method: "PUT",
      url,
      data,
      headers,
      isAutoCatchStatus
    })
  }

  /**
   * Delete请求
   * @param url
   * @param data
   * @param methodsConfig
   */
  delete(url, data = {}, methodsConfig = {}) {
    const { headers, isAutoCatchStatus = true } = methodsConfig
    return this.responseProcess({
      method: "DELETE",
      url,
      data,
      headers,
      isAutoCatchStatus
    })
  }

  /**
   * 上传文件方法
   * @param url
   * @param data
   * @param methodsConfig
   */
  uploadFile(url, data, methodsConfig = {}) {
    const { headers = { "content-type": "multipart/form-data" }, isAutoCatchStatus = true } = methodsConfig
    return this.responseProcess({
      method: "POST",
      url,
      data,
      headers,
      isAutoCatchStatus
    })
  }
}

export default HttpService
